import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, adminAuth } from "@/lib/api";
import { TID } from "@/lib/testIds";
import {
  Sparkles, LogOut, LayoutDashboard, Users, ClipboardList, CalendarDays,
  Loader2, ArrowUpRight, CheckCircle2, XCircle, Truck,
} from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "leads", label: "Leads", icon: Users },
  { key: "bookings", label: "Bookings", icon: ClipboardList },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
];

const LEAD_STATUSES = ["New", "Contacted", "Estimate Sent", "Deposit Pending", "Booking Requested", "Confirmed", "Lost", "Future Event"];
const BOOKING_STATUSES = ["New Lead", "Deposit Pending", "Pending Review", "More Information Needed", "Confirmed", "Delivery Scheduled", "Delivered", "Event in Progress", "Pickup Scheduled", "Completed", "Cancelled"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!adminAuth.isAuthed()) { nav("/admin/login"); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, l, b, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/leads"),
        api.get("/admin/bookings"),
        api.get("/admin/calendar"),
      ]);
      setStats(s.data); setLeads(l.data); setBookings(b.data); setCalendar(c.data);
    } catch (err) {
      if (err?.response?.status === 401) { adminAuth.clear(); nav("/admin/login"); return; }
      toast.error("Failed to load dashboard");
    } finally { setLoading(false); }
  };

  const logout = () => { adminAuth.clear(); nav("/admin/login"); };

  const updateLead = async (id, patch) => {
    try {
      await api.patch(`/admin/leads/${id}`, patch);
      toast.success("Lead updated");
      loadAll();
    } catch { toast.error("Update failed"); }
  };
  const updateBooking = async (id, patch) => {
    try {
      await api.patch(`/admin/bookings/${id}`, patch);
      toast.success("Booking updated");
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="min-h-screen bg-sand">
      <aside className="fixed top-0 left-0 bottom-0 w-64 bg-charcoal text-ivory p-6 hidden md:flex flex-col">
        <div className="flex items-center gap-2 mb-10">
          <Sparkles className="h-5 w-5 text-champagne" />
          <div>
            <div className="h-display text-lg leading-tight">SUE Admin</div>
            <div className="overline text-ivory/60">Luxury Portable Restrooms</div>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {TABS.map((t) => {
            const on = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
                className={`w-full flex items-center gap-3 rounded-full px-4 py-3 text-sm transition-colors ${on ? "bg-champagne text-charcoal" : "text-ivory/80 hover:bg-white/10"}`}>
                <t.icon className="h-4 w-4" />{t.label}
              </button>
            );
          })}
        </nav>
        <div className="pt-6 border-t border-white/10">
          <div className="text-xs text-ivory/60 mb-3">{adminAuth.getEmail()}</div>
          <button onClick={logout} data-testid={TID.adminLogoutBtn} className="w-full flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm hover:bg-white/10">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="md:ml-64">
        <header className="p-6 md:p-10 border-b border-stone bg-ivory/70 backdrop-blur-sm sticky top-0 z-10 flex items-center justify-between">
          <div>
            <div className="overline">Dashboard</div>
            <h1 className="h-display text-2xl md:text-3xl mt-1 capitalize">{tab}</h1>
          </div>
          <Link to="/" className="text-sm text-graphite hover:text-charcoal inline-flex items-center gap-1">View site <ArrowUpRight className="h-3 w-3" /></Link>
        </header>

        <main className="p-6 md:p-10">
          {loading ? (
            <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-pebble" /></div>
          ) : (
            <>
              {tab === "overview" && <Overview stats={stats} bookings={bookings} leads={leads} />}
              {tab === "leads" && <LeadsView leads={leads} onUpdate={updateLead} />}
              {tab === "bookings" && <BookingsView bookings={bookings} onUpdate={updateBooking} />}
              {tab === "calendar" && <CalendarView events={calendar} />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint }) {
  return (
    <div className="card-soft p-6">
      <div className="overline">{label}</div>
      <div className="h-display text-4xl mt-2">{value}</div>
      {hint && <div className="text-xs text-graphite mt-2">{hint}</div>}
    </div>
  );
}

function Overview({ stats, bookings, leads }) {
  if (!stats) return null;
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total Leads" value={stats.total_leads} hint={`${stats.new_leads} new`} />
        <Kpi label="Pending Bookings" value={stats.pending_bookings} hint="Awaiting review" />
        <Kpi label="Confirmed Bookings" value={stats.confirmed_bookings} hint="Ready to deliver" />
        <Kpi label="Deposits Collected" value={`$${Number(stats.deposits_collected).toFixed(0)}`} hint="Lifetime" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-soft p-6">
          <div className="flex items-center justify-between">
            <div className="overline">Latest Bookings</div>
            <span className="text-xs text-graphite">{bookings.length} total</span>
          </div>
          <div className="mt-4 space-y-3">
            {bookings.slice(0, 5).map((b) => (
              <div key={b.id} className="flex items-center justify-between border-b border-stone last:border-0 pb-3 last:pb-0">
                <div>
                  <div className="font-medium">{b.customer_name}</div>
                  <div className="text-xs text-graphite">{b.event_type} · {b.event_date} · {b.event_city}</div>
                </div>
                <div className="text-xs px-3 py-1 rounded-full bg-champagne/60">{b.status}</div>
              </div>
            ))}
            {bookings.length === 0 && <div className="text-sm text-graphite">No bookings yet.</div>}
          </div>
        </div>

        <div className="card-soft p-6">
          <div className="flex items-center justify-between">
            <div className="overline">Latest Leads</div>
            <span className="text-xs text-graphite">{leads.length} total</span>
          </div>
          <div className="mt-4 space-y-3">
            {leads.slice(0, 5).map((l) => (
              <div key={l.id} className="flex items-center justify-between border-b border-stone last:border-0 pb-3 last:pb-0">
                <div>
                  <div className="font-medium">{l.name}</div>
                  <div className="text-xs text-graphite">{l.event_type} · {l.event_date || "—"} · {l.event_city || "—"}</div>
                </div>
                <div className="text-xs px-3 py-1 rounded-full bg-sand">{l.status}</div>
              </div>
            ))}
            {leads.length === 0 && <div className="text-sm text-graphite">No leads yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function LeadsView({ leads, onUpdate }) {
  return (
    <div className="card-soft overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-sand text-graphite text-left">
          <tr>
            <th className="p-4">Name</th><th className="p-4">Contact</th><th className="p-4">Event</th><th className="p-4">Date/City</th><th className="p-4">Status</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => (
            <tr key={l.id} className="border-t border-stone" data-testid={`lead-row-${l.id}`}>
              <td className="p-4">{l.name}</td>
              <td className="p-4"><div>{l.email}</div><div className="text-xs text-graphite">{l.phone}</div></td>
              <td className="p-4">{l.event_type}<div className="text-xs text-graphite">{l.trailers || "—"}</div></td>
              <td className="p-4">{l.event_date || "—"}<div className="text-xs text-graphite">{l.event_city || "—"}</div></td>
              <td className="p-4">
                <select value={l.status} onChange={(e) => onUpdate(l.id, { status: e.target.value })}
                  className="rounded-full border border-stone bg-white px-3 py-1 text-xs">
                  {LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
            </tr>
          ))}
          {leads.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-graphite">No leads yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function BookingsView({ bookings, onUpdate }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <div className="space-y-4">
      {bookings.map((b) => {
        const open = expanded === b.id;
        return (
          <div key={b.id} className="card-soft" data-testid={`booking-row-${b.id}`}>
            <button className="w-full p-6 text-left flex items-center justify-between" onClick={() => setExpanded(open ? null : b.id)}>
              <div className="flex-1 grid md:grid-cols-4 gap-3 items-center">
                <div>
                  <div className="font-medium">{b.customer_name}</div>
                  <div className="text-xs text-graphite">{b.customer_email}</div>
                </div>
                <div className="text-sm">{b.event_type}<div className="text-xs text-graphite">{b.event_date}</div></div>
                <div className="text-sm">{b.event_city}<div className="text-xs text-graphite">{b.guest_count} guests · {b.trailers_requested === 2 ? "2 trailers" : "1 trailer"}</div></div>
                <div><span className="text-xs px-3 py-1 rounded-full bg-champagne/60">{b.status}</span></div>
              </div>
            </button>
            {open && (
              <div className="border-t border-stone p-6 grid md:grid-cols-2 gap-6">
                <div>
                  <div className="overline">Details</div>
                  <dl className="mt-3 text-sm space-y-1">
                    <Row2 k="Phone" v={b.customer_phone} />
                    <Row2 k="Venue" v={`${b.venue_name || "—"}, ${b.event_address}, ${b.event_city}`} />
                    <Row2 k="Guests" v={b.guest_count} />
                    <Row2 k="Add-ons" v={(b.addons || []).join(", ") || "None"} />
                    <Row2 k="Deposit" v={`$${Number(b.deposit_amount).toFixed(2)} ${b.deposit_paid ? "(paid)" : "(pending)"}`} />
                    <Row2 k="Total est." v={`$${Number(b.total).toFixed(2)}`} />
                  </dl>
                </div>
                <div className="space-y-3">
                  <div className="overline">Manage</div>
                  <label className="block text-sm">
                    <span className="text-graphite">Status</span>
                    <select value={b.status} onChange={(e) => onUpdate(b.id, { status: e.target.value })}
                      className="mt-1 w-full rounded-2xl border border-stone bg-white px-3 py-2 text-sm">
                      {BOOKING_STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </label>
                  <div>
                    <div className="text-sm text-graphite mb-1">Assign trailers</div>
                    <div className="flex gap-2">
                      {["trailer_1", "trailer_2"].map((t) => {
                        const on = (b.assigned_trailers || []).includes(t);
                        return (
                          <button key={t}
                            onClick={() => {
                              const set = new Set(b.assigned_trailers || []);
                              on ? set.delete(t) : set.add(t);
                              onUpdate(b.id, { assigned_trailers: Array.from(set) });
                            }}
                            className={`flex-1 rounded-full px-4 py-2 text-sm border ${on ? "bg-charcoal text-ivory border-charcoal" : "bg-white text-charcoal border-stone hover:border-pebble"}`}>
                            {t === "trailer_1" ? "Trailer 1" : "Trailer 2"}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button onClick={() => onUpdate(b.id, { status: "Confirmed" })} className="flex-1 rounded-full bg-champagne text-charcoal px-4 py-2 text-sm hover:bg-pebble transition-colors inline-flex items-center justify-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> Confirm
                    </button>
                    <button onClick={() => onUpdate(b.id, { status: "Delivery Scheduled" })} className="flex-1 rounded-full bg-white text-charcoal border border-stone px-4 py-2 text-sm inline-flex items-center justify-center gap-2">
                      <Truck className="h-4 w-4" /> Schedule delivery
                    </button>
                    <button onClick={() => onUpdate(b.id, { status: "Cancelled" })} className="rounded-full bg-white text-charcoal border border-stone px-4 py-2 text-sm hover:border-destructive hover:text-destructive inline-flex items-center justify-center gap-2">
                      <XCircle className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {bookings.length === 0 && <div className="text-center py-16 text-graphite">No bookings yet.</div>}
    </div>
  );
}

function Row2({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <dt className="text-graphite">{k}</dt>
      <dd className="text-charcoal text-right">{String(v)}</dd>
    </div>
  );
}

function CalendarView({ events }) {
  const byTrailer = { trailer_1: [], trailer_2: [], unassigned: [] };
  events.forEach((e) => (byTrailer[e.trailer] || byTrailer.unassigned).push(e));
  const columns = [
    { key: "trailer_1", title: "Trailer 1" },
    { key: "trailer_2", title: "Trailer 2" },
    { key: "unassigned", title: "Unassigned" },
  ];
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {columns.map((c) => (
        <div key={c.key} className="card-soft p-6">
          <div className="overline">{c.title}</div>
          <div className="mt-4 space-y-3">
            {(byTrailer[c.key] || []).sort((a,b) => (a.event_date || "").localeCompare(b.event_date || "")).map((e) => (
              <div key={`${e.booking_id}-${c.key}`} className="rounded-2xl border border-stone p-4 bg-white">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">{e.customer_name}</div>
                  <div className="text-[10px] px-2 py-0.5 rounded-full bg-sand">{e.status}</div>
                </div>
                <div className="text-xs text-graphite mt-1">{e.event_type}</div>
                <div className="text-xs text-charcoal mt-2">{e.event_date}{e.event_end_date ? ` → ${e.event_end_date}` : ""}</div>
              </div>
            ))}
            {(byTrailer[c.key] || []).length === 0 && <div className="text-sm text-graphite">No events.</div>}
          </div>
        </div>
      ))}
    </div>
  );
}
